import type { ThemeDefinition } from "../theme-definition.type";
import { saads, saadsRed, saadsGreen, slate, indigo, purple, graphite } from "./saads";
import { teal, violet } from "./teal";

const cyan: ThemeDefinition = {
  id: "cyan",
  name: "Cyan",
  light: {
    "background": "#f7fbfc",
    "foreground": "#0e1b1f",
    "card": "#ffffff",
    "muted": "#e8f2f6",
    "muted-foreground": "#54676d",
    "border": "#d9e3e7",
    "input": "#878f92",
    "primary": "#266d81",
    "primary-foreground": "#ffffff",
    "accent": "#def3fa",
    "accent-foreground": "#003441",
    "brand": "#004159",
    "brand-foreground": "#ffffff",
    "ring": "#468ba0",
    "success": "#3a732c",
    "warning": "#875814",
    "destructive": "#ac2f2f",
    "info": "#296898",
    "popover": "#ffffff",
    "link": "#266d81",
  },
  dark: {
    "background": "#141718",
    "foreground": "#edf1f2",
    "card": "#1d2122",
    "popover": "#252b2d",
    "muted": "#2c3133",
    "muted-foreground": "#acb2b5",
    "border": "#3a4346",
    "input": "#6b6e6f",
    "primary": "#007cb3",
    "primary-foreground": "#ffffff",
    "link": "#63cae9",
    "accent": "#003d4d",
    "accent-foreground": "#c3edfb",
    "brand": "#003149",
    "brand-foreground": "#eef7fa",
    "ring": "#2aa8c9",
    "success": "#7fc76f",
    "warning": "#e6b55d",
    "destructive": "#f48780",
    "info": "#7db4e4",
  },
};

const brightBlue: ThemeDefinition = {
  id: "bright-blue",
  name: "Bright Blue",
  light: {
    "background": "#ffffff",
    "foreground": "#212529",
    "card": "#ffffff",
    "muted": "#eeeeee",
    "muted-foreground": "#5a6169",
    "border": "#dee2e6",
    "input": "#909294",
    "primary": "#007bc4",
    "primary-foreground": "#ffffff",
    "accent": "#e6f1fa",
    "accent-foreground": "#00406b",
    "brand": "#002b49",
    "brand-foreground": "#ffffff",
    "ring": "#007bc4",
    "success": "#2f7a1c",
    "warning": "#8a5a00",
    "destructive": "#b22534",
    "info": "#2563a8",
    "popover": "#ffffff",
    "link": "#006fb0",
  },
  dark: {
    "background": "#141618",
    "foreground": "#eef0f3",
    "card": "#1e2123",
    "popover": "#262b2e",
    "muted": "#2c3135",
    "muted-foreground": "#adb2b6",
    "border": "#3b4247",
    "input": "#6b6d70",
    "primary": "#0077da",
    "primary-foreground": "#ffffff",
    "link": "#77c3fb",
    "accent": "#0b3956",
    "accent-foreground": "#c8eaff",
    "brand": "#002b55",
    "brand-foreground": "#f0f6fb",
    "ring": "#4ca0dc",
    "success": "#7fc76f",
    "warning": "#e6b55d",
    "destructive": "#f48780",
    "info": "#7db4e4",
  },
};

const oceanBlue: ThemeDefinition = {
  id: "ocean-blue",
  name: "Ocean Blue",
  light: {
    "background": "#f8fafd",
    "foreground": "#111921",
    "card": "#ffffff",
    "muted": "#eaf1f8",
    "muted-foreground": "#586571",
    "border": "#dbe2e9",
    "input": "#898e93",
    "primary": "#336892",
    "primary-foreground": "#ffffff",
    "accent": "#e0f1ff",
    "accent-foreground": "#0a304c",
    "brand": "#003a6c",
    "brand-foreground": "#ffffff",
    "ring": "#5186b1",
    "success": "#3a732c",
    "warning": "#875814",
    "destructive": "#ac2f2f",
    "info": "#296898",
    "popover": "#ffffff",
    "link": "#336892",
  },
  dark: {
    "background": "#141618",
    "foreground": "#eef0f3",
    "card": "#1e2123",
    "popover": "#262b2e",
    "muted": "#2d3135",
    "muted-foreground": "#adb2b6",
    "border": "#3b4248",
    "input": "#6b6d70",
    "primary": "#0073e6",
    "primary-foreground": "#ffffff",
    "link": "#74c2ff",
    "accent": "#07385a",
    "accent-foreground": "#c7e9ff",
    "brand": "#002a5a",
    "brand-foreground": "#f0f6fc",
    "ring": "#479fe4",
    "success": "#7fc76f",
    "warning": "#e6b55d",
    "destructive": "#f48780",
    "info": "#7db4e4",
  },
};

const aubergine: ThemeDefinition = {
  id: "aubergine",
  name: "Aubergine",
  light: {
    "background": "#fcf9fb",
    "foreground": "#1e151d",
    "card": "#ffffff",
    "muted": "#f5edf4",
    "muted-foreground": "#6c5e6a",
    "border": "#e6dfe5",
    "input": "#918c90",
    "primary": "#7f507b",
    "primary-foreground": "#ffffff",
    "accent": "#fae8f7",
    "accent-foreground": "#40203d",
    "brand": "#561d52",
    "brand-foreground": "#ffffff",
    "ring": "#9e6d99",
    "success": "#3a732c",
    "warning": "#875814",
    "destructive": "#ac2f2f",
    "info": "#296898",
    "popover": "#ffffff",
    "link": "#7f507b",
  },
  dark: {
    "background": "#171517",
    "foreground": "#f2eff1",
    "card": "#221f22",
    "popover": "#2d292c",
    "muted": "#332f32",
    "muted-foreground": "#b4afb3",
    "border": "#453f44",
    "input": "#6f6c6e",
    "primary": "#c039b9",
    "primary-foreground": "#ffffff",
    "link": "#e69ee0",
    "accent": "#4b2548",
    "accent-foreground": "#fbd8f6",
    "brand": "#440b42",
    "brand-foreground": "#f9f3f8",
    "ring": "#c579bf",
    "success": "#7fc76f",
    "warning": "#e6b55d",
    "destructive": "#f48780",
    "info": "#7db4e4",
  },
};

const brick: ThemeDefinition = {
  id: "brick",
  name: "Brick",
  light: {
    "background": "#fdf9f8",
    "foreground": "#211512",
    "card": "#ffffff",
    "muted": "#f8edeb",
    "muted-foreground": "#715e5a",
    "border": "#e9dfdc",
    "input": "#938c8a",
    "primary": "#8b5243",
    "primary-foreground": "#ffffff",
    "accent": "#ffe9e3",
    "accent-foreground": "#472218",
    "brand": "#611f0e",
    "brand-foreground": "#ffffff",
    "ring": "#aa6f5f",
    "success": "#3a732c",
    "warning": "#875814",
    "destructive": "#ac2f2f",
    "info": "#296898",
    "popover": "#ffffff",
    "link": "#8b5243",
  },
  dark: {
    "background": "#181514",
    "foreground": "#f3efee",
    "card": "#231f1e",
    "popover": "#2e2927",
    "muted": "#352f2d",
    "muted-foreground": "#b6afae",
    "border": "#483f3c",
    "input": "#706c6b",
    "primary": "#da3d0f",
    "primary-foreground": "#ffffff",
    "link": "#f8a18a",
    "accent": "#53261b",
    "accent-foreground": "#ffdace",
    "brand": "#4f0d00",
    "brand-foreground": "#fcf3f1",
    "ring": "#d77b64",
    "success": "#7fc76f",
    "warning": "#e6b55d",
    "destructive": "#f48780",
    "info": "#7db4e4",
  },
};

const amber: ThemeDefinition = {
  id: "amber",
  name: "Amber",
  light: {
    "background": "#fcfaf7",
    "foreground": "#1e170e",
    "card": "#ffffff",
    "muted": "#f5efe7",
    "muted-foreground": "#6c6154",
    "border": "#e6e0d9",
    "input": "#918d87",
    "primary": "#7b5d31",
    "primary-foreground": "#ffffff",
    "accent": "#f8edde",
    "accent-foreground": "#3d290a",
    "brand": "#522f00",
    "brand-foreground": "#ffffff",
    "ring": "#9a7a4e",
    "success": "#3a732c",
    "warning": "#875814",
    "destructive": "#ac2f2f",
    "info": "#296898",
    "popover": "#ffffff",
    "link": "#7b5d31",
  },
  dark: {
    "background": "#171614",
    "foreground": "#f2f0ed",
    "card": "#22201d",
    "popover": "#2d2a25",
    "muted": "#33302b",
    "muted-foreground": "#b4b0ac",
    "border": "#45403a",
    "input": "#6f6d6b",
    "primary": "#b26100",
    "primary-foreground": "#ffffff",
    "link": "#e0b270",
    "accent": "#483008",
    "accent-foreground": "#f7e1c4",
    "brand": "#411f00",
    "brand-foreground": "#f9f4ee",
    "ring": "#bf8e45",
    "success": "#7fc76f",
    "warning": "#e6b55d",
    "destructive": "#f48780",
    "info": "#7db4e4",
  },
};

// Order matters for the picker; the default theme comes first.
export const THEMES: readonly ThemeDefinition[] = [
  saads,
  saadsRed,
  saadsGreen,
  teal,
  cyan,
  oceanBlue,
  brightBlue,
  slate,
  indigo,
  violet,
  purple,
  aubergine,
  brick,
  amber,
  graphite,
];
